import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { UseRadioGroupTransparent } from '../../lib/formik/Radio';
import { APPLICATION_TYPE, SAVE_LATER_VALIDATION_CHECK } from '../../utils/constant';
import { MFTextField } from '../../lib/formik';
import { ProceedSaveLater } from './components';
import { useDispatch, useSelector } from 'react-redux';
import { QibQue } from '../../redux-store/actions';
import { RootStateType } from '../../redux-store/reducers';
import {
  applicationComparison,
  checkValidationBasedOnDate,
  saveForLater,
} from '../../utils/utilityFunctions';
import { updateApplication } from '../../redux-store/actions/application';
import { useHistory, useLocation } from 'react-router';
import { useSnackbar } from 'notistack';
import { QibDetailsType } from '../../redux-store/types/api-types';
import {
  Box,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
} from '@mui/material';
import { _buildQibValidationSchema } from '../../utils/schema';
import { useFeatureToggles } from '../../utils/useDataMdms';

export type Values = QibDetailsType;
export type _captureIfType = {
  id: string;
  value: string;
  [key: string]: string;
};

export type _singleQibQuestionType = {
  id: string;
  label: string;
  type: 'yesno' | 'multi' | 'text';
  options?: string[];
  remarks?: boolean;
  captureIf?: _captureIfType[];
  [key: string]: string | _captureIfType[] | boolean | string[] | undefined;
};

export type QibQuestionType = {
  section: string;
  remarkLabel?: string;
  applicationType?: 'individual' | 'non_individual';
  questions: _singleQibQuestionType[];
  [key: string]: string | _singleQibQuestionType[] | undefined;
};

export type QibValues = {
  qib?: Values;
  qibQue: QibQuestionType[];
  saveType: 'save for later' | 'save and proceed';
  [key: string]: Values | QibQuestionType[] | string | undefined;
};

export const shouldShowQuestion = (q: _singleQibQuestionType, values: QibValues): boolean => {
  if (!q.captureIf?.length) return true;

  return q.captureIf.every((c: _captureIfType) => values?.qib?.[c?.id] === c.value);
};

export const shouldShowRemarks = (q: _singleQibQuestionType, values: QibValues): boolean => {
  if (!q.remarks) return false;

  const val = values.qib?.[q.id];

  if (q.type === 'yesno') return val === 'yes';
  if (q.type === 'multi') return !!val && val !== 'None of above';

  return false;
};

export type _Props = {
  values: QibValues;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const QibForm: React.FC<_Props> = ({ values, handleChange }) => {
  const showQ = (q: _singleQibQuestionType) => shouldShowQuestion(q, values);

  const showR = (q: _singleQibQuestionType) => shouldShowRemarks(q, values);

  return (
    <Box>
      {values.qibQue.map((section: QibQuestionType, i: number) => (
        <Box key={i} sx={{ mb: 4 }}>
          {/* Section Title */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            {section.section}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Question</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Response</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                    {section.remarkLabel ?? 'Remarks'}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {section.questions.map((q) => {
                  if (!showQ(q)) return null;

                  return (
                    <TableRow key={q.id}>
                      {/* Question Label */}
                      <TableCell sx={{ verticalAlign: 'top' }}>{q.label}</TableCell>

                      {/* Response */}
                      <TableCell>
                        {/* YES/NO */}
                        {q.type === 'yesno' && (
                          <UseRadioGroupTransparent
                            formLabel=""
                            name={`qib.${q.id}`}
                            value={values.qib?.[q.id] ?? ''}
                            items={[
                              { label: 'Yes', value: 'yes' },
                              { label: 'No', value: 'no' },
                            ]}
                          />
                        )}

                        {/* MULTI */}
                        {q.type === 'multi' && q.options && (
                          <UseRadioGroupTransparent
                            formLabel=""
                            name={`qib.${q.id}`}
                            value={values.qib?.[q.id] ?? ''}
                            items={q.options.map((o) => ({
                              label: o,
                              value: o,
                            }))}
                          />
                        )}

                        {/* TEXT */}
                        {q.type === 'text' && (
                          <MFTextField
                            name={`qib.${q.id}`}
                            value={values.qib?.[q.id] ?? ''}
                            onChange={handleChange}
                            placeholder={`Enter ${q.label}`}
                          />
                        )}
                      </TableCell>

                      {/* Remarks */}
                      <TableCell>
                        {showR(q) && (
                          <MFTextField
                            name={`qib.${q.id}Remarks`}
                            value={values.qib?.[`${q.id}Remarks`] ?? ''}
                            onChange={handleChange}
                            placeholder="Enter remarks"
                            multiline
                            minRows={2}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export const _initialQibValues = {
  id: null,
  applicationId: null,

  isCitizenOfResidingCountry: '',
  isCitizenOfResidingCountryRemarks: '',
  isPartOfSameGroup: '',
  isPartOfSameGroupRemarks: '',
  isInvestorContribute50Percent: '',
  isInvestorContribute50PercentRemarks: '',

  isQualifiedBuyer: '',
  isQualifiedBuyerRemarks: '',
  isBeneficialOwner: '',
  isBeneficialOwnerRemarks: '',
  isPartOfGroup: '',
  isPartOfGroupRemarks: '',
  isInvestedInNeoAif: '',
  isInvestedInNeoAifRemarks: '',
  isControlledByAny: '',
  isControlledByAnyRemarks: '',

  isOutsideFundSetUp: '',
  isOutsideFundSetUpRemarks: '',
  outsideFundInvestor50Percent: '',
  outsideFundInvestor50PercentRemarks: '',
  outsideFundInvestor25Percent: '',
  outsideFundInvestor25PercentRemarks: '',
  outsideFundInvestor25Percent_optionB: '',
  outsideFundInvestor25Percent_optionBRemarks: '',
  outsideFundInvestor50Percent_optionB: '',
  outsideFundInvestor50Percent_optionBRemarks: '',
};

export default function QibDetailsScreen(): JSX.Element {
  const [loading, setLoading] = React.useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [initialQibValues, setInitialQibValues] = React.useState<QibValues>({
    qib: _initialQibValues,
    qibQue: [] as unknown as QibQuestionType[],
    saveType: 'save for later',
  });
  const dispatch = useDispatch();
  const { application } = useSelector((store: RootStateType) => store.application);
  const {
    auth: { role },
  } = useSelector((store: RootStateType) => store);
  const [qibJson, setQibJson] = React.useState<QibQuestionType[]>([]);
  useEffect(() => {
    // Fetch existing data from API and set as initial values
    async function fetchQibData() {
      setLoading(true);
      try {
        // Simulate API call
        const res = ((await dispatch(QibQue())) || []) as unknown as QibQuestionType[];
        setQibJson(res);
      } catch (error) {
        console.error('Error fetching QIB data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchQibData();
  }, []);
  useEffect(() => {
    if (Object.keys(application?.qibDetail || {}).length > 0) {
      setInitialQibValues((prev) => ({
        ...prev,
        qib: {
          ...prev.qib,
          ...application?.qibDetail,
        } as QibDetailsType,
        qibQue: qibJson.filter(
          (section) =>
            section.applicationType === application?.applicationType || !section.applicationType
        ),
      }));
    }
  }, [application, qibJson]);

  function buildQibPayloadForBE(values: QibValues, qibJson: QibQuestionType[]) {
    // eslint-disable-next-line
    const payload: any = { qib: {} };

    qibJson.forEach((section) => {
      section.questions.forEach((q) => {
        const isVisible = shouldShowQuestion(q, values);
        const isRemarksVisible = shouldShowRemarks(q, values);

        // QUESTION
        if (isVisible) {
          const key = q.id;
          const val = values.qib?.[key];

          if (val !== undefined && val !== null && val !== '') {
            payload.qib[key] = val;
          }
        }

        // REMARKS
        if (isRemarksVisible) {
          const remarksKey = `${q.id}Remarks`;
          const remarksVal = values.qib?.[remarksKey];

          if (remarksVal !== undefined && remarksVal !== null && remarksVal !== '') {
            payload.qib[remarksKey] = remarksVal;
          }
        }
      });
    });

    return payload;
  }

  const { toggler } = useFeatureToggles();
  const handleSubmit = async (values: QibValues) => {
    try {
      const {
        id,
        applicant1ReferenceId = '',
        currentStep,
        applicationNumber,
        applicationType,
        createdAt = '',
      } = application || {};
      const { qib, saveType } = values;
      // Non-individual QIB comes after Document Details, so its index shifts
      // by one when the Excuse & Exclusion section is enabled.
      const excuseOffset = checkValidationBasedOnDate(
        createdAt,
        toggler.dateFeatureToggles.excuseExclusionValidationDate
      )
        ? 1
        : 0;

      const updatedPayload = {
        qibDetail: {
          ..._initialQibValues,
          ...(qib?.id ? { id: Number(qib?.id) } : {}),
          ...buildQibPayloadForBE(values, initialQibValues.qibQue).qib,
          applicationId: id ? Number(id) : undefined,
        },
      };
      const isSaveLater = saveType !== 'save and proceed';
      const checkApplication = applicationComparison(application, {
        ...application,
        ...updatedPayload,
        currentStep:
          applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
            ? !!currentStep && currentStep > 5 + excuseOffset
              ? currentStep
              : Number(currentStep) + 1
            : !!currentStep && currentStep > 8
            ? currentStep
            : Number(currentStep) + 1,
      });
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              ...updatedPayload,
              // status: statusRoleBased(role, application),
              currentStep:
                applicationType === APPLICATION_TYPE.NON_INDIVIDUAL ? 9 + excuseOffset : 6,
              //!!currentStep && currentStep > 4 ? currentStep : Number(currentStep) + 1,
            },
            applicationId: id,
            ...(isSaveLater && { toastMessage: '' }),
          })
        );
        saveType === 'save and proceed'
          ? history.push(
              application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                ? 'fatca'
                : 'nominee-details',
              {
                id,
                applicant1ReferenceId,
              }
            )
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(
            `Application ${application?.applicationNumber} - ` + ' Saved successfully',
            {
              variant: 'success',
              autoHideDuration: 3000,
            }
          );
        }
        !isSaveLater
          ? history.push('nominee-details', {
              id,
              applicant1ReferenceId,
            })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      }
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Formik
      initialValues={initialQibValues}
      validationSchema={_buildQibValidationSchema(initialQibValues.qibQue)}
      enableReinitialize={true}
      onSubmit={handleSubmit}>
      {({ values, handleChange, setValues }) => (
        <Form>
          <QibForm values={values} handleChange={handleChange} />

          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Form>
      )}
    </Formik>
  );
}
